// SignupPage.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../Home/ThemeContext'; 
import { GraduationCap, Users, Building2, ChevronDown, CheckCircle, Award, BookOpen, Zap } from 'lucide-react';

// Import the form components
import Student from './Student';
import Partner from './Partner';
import College from './College';

// --- 1. GOOGLE ICON SVG (Reused here) ---
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function SignupPage() {
  const { isDarkMode } = useTheme(); 
  const [userType, setUserType] = useState('student'); 
  const [formData, setFormData] = useState({});
  const [isTypeLocked, setIsTypeLocked] = useState(false); 

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');
    
    if (type && ['student', 'partner', 'college'].includes(type)) {
      setUserType(type);
      setIsTypeLocked(true); 
    } else {
      setIsTypeLocked(false); 
    }
  }, []); 

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- 2. GLOBAL GOOGLE SIGNUP HANDLER ---
  const handleGoogleSignup = () => {
    // This dynamically passes the userType (student/college/partner) to your backend
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    console.log(`Initiating Google Signup for role: ${userType}`);
    window.location.href = `${baseUrl}/auth/google?role=${userType}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Logic handled inside sub-components usually, but if handled here:
    console.log('Form submitted:', { userType, ...formData });
  };

  const userTypes = [
    { value: 'student', label: 'Student Portal', desc: 'Access courses, applications & more', icon: GraduationCap },
    { value: 'partner', label: 'Partner Portal', desc: 'Counselors, agents & institutions', icon: Users },
    { value: 'college', label: 'College Registration', desc: 'Register your institution', icon: Building2 }
  ];

  return (
    <div className={`min-h-screen pt-32 pb-12 px-4 transition-colors duration-300 relative overflow-hidden ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      {/* Background Blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-blob"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          
          {/* Left Side Content (Unchanged) */}
          <div className="lg:col-span-2 space-y-6 hidden lg:block">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div className="col-span-2">
                <h2 className={`text-3xl md:text-4xl font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Join Vidyazon Today
                </h2>
                <p className={`text-base md:text-lg mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Start your journey with India's leading education platform
                </p>
              </div>
            </div>
            
            {/* Features List */}
            <div className="space-y-4">
              <div className={`p-4 rounded-xl backdrop-blur-sm transition-all duration-300 hover:scale-105 ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-blue-50/50 border border-blue-100'}`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg"><CheckCircle className="w-5 h-5 text-white" /></div>
                  <div><h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-black'}`}>Instant Access</h3><p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Get immediate access to thousands of courses</p></div>
                </div>
              </div>
              <div className={`p-4 rounded-xl backdrop-blur-sm transition-all duration-300 hover:scale-105 animation-delay-200 ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-blue-50/50 border border-blue-100'}`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg"><Award className="w-5 h-5 text-white" /></div>
                  <div><h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-black'}`}>Certified Learning</h3><p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Earn recognized certificates</p></div>
                </div>
              </div>
              {/* Add other feature cards here... */}
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:col-span-3">
            <div className={`rounded-2xl shadow-2xl overflow-hidden backdrop-blur-sm ${
              isDarkMode ? 'bg-gray-800/90 border border-gray-700' : 'bg-white/90 border border-gray-200'
            }`}>
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 relative z-10">Create Your Account</h1>
                <p className="text-blue-100 relative z-10">Join Vidyazon Innovation Hub today</p>
              </div>

              <div className="p-6 md:p-8">
                
                {/* Account Type Selector */}
                {!isTypeLocked && (
                  <div className="mb-6">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      Select Account Type *
                    </label>
                    <div className="relative">
                      <select
                        value={userType}
                        onChange={(e) => {
                          setUserType(e.target.value);
                          setFormData({}); 
                        }}
                        required
                        className={`w-full px-4 py-4 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none text-base font-medium ${
                          isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-black'
                        }`}
                      >
                        {userTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label} - {type.desc}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className={`absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                  </div>
                )}

                {/* Dynamic Form Fields */}
                <form onSubmit={handleSubmit} className="animate-fadeIn">
                    {/* Render specific form based on userType */}
                    {userType === 'student' && <Student isDarkMode={isDarkMode} handleInputChange={handleInputChange} />}
                    {userType === 'partner' && <Partner isDarkMode={isDarkMode} handleInputChange={handleInputChange} />}
                    {userType === 'college' && <College isDarkMode={isDarkMode} handleInputChange={handleInputChange} />}

                    {/* --- 3. GLOBAL GOOGLE SIGNUP SECTION START --- */}
                    <div className="mt-8">
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className={`w-full border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className={`px-2 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                            OR
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleGoogleSignup}
                        className={`mt-6 w-full flex items-center justify-center gap-3 py-3.5 px-6 rounded-lg border-2 transition-all font-semibold ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700' 
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <GoogleIcon />
                        {/* Dynamic text based on role */}
                        <span>Sign up as {userType.charAt(0).toUpperCase() + userType.slice(1)} with Google</span>
                      </button>
                    </div>
                    {/* --- GLOBAL GOOGLE SIGNUP SECTION END --- */}

                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .bg-grid-pattern {
          background-image: linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 10s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </div>
  );
}